/**
 * Hook de los conductores de una flota (US-2.3). Paginación server-side con el `meta` del
 * contrato; la red vive en `fleetApi`.
 */
import { useCallback, useEffect, useState } from 'react';
import type { FleetDriver, PageMeta } from '@goelectric/shared-types';
import { ApiError } from '@/lib/http/apiClient';
import { fleetApi } from '../api/fleetApi';

/** Límite por página por defecto de conductores. */
export const DEFAULT_FLEET_DRIVERS_LIMIT = 20;

export interface UseFleetDriversOptions {
  limit?: number;
}

export interface UseFleetDriversResult {
  data: FleetDriver[];
  meta: PageMeta;
  loading: boolean;
  error: string | null;
  offset: number;
  limit: number;
  setOffset: (offset: number) => void;
  refresh: () => void;
}

const emptyMeta = (limit: number): PageMeta => ({ offset: 0, limit, total: 0, hasMore: false });

function toErrorMessage(error: unknown): string {
  if (error instanceof ApiError) {
    return error.message;
  }
  if (error instanceof Error && error.message) {
    return error.message;
  }
  return 'No se pudieron cargar los conductores.';
}

export function useFleetDrivers(
  fleetId: string | undefined,
  options: UseFleetDriversOptions = {},
): UseFleetDriversResult {
  const limit = options.limit ?? DEFAULT_FLEET_DRIVERS_LIMIT;
  const [offset, setOffsetState] = useState(0);
  const [data, setData] = useState<FleetDriver[]>([]);
  const [meta, setMeta] = useState<PageMeta>(() => emptyMeta(limit));
  const [loading, setLoading] = useState(Boolean(fleetId));
  const [error, setError] = useState<string | null>(null);
  const [reloadKey, setReloadKey] = useState(0);

  useEffect(() => {
    if (!fleetId) {
      setData([]);
      setMeta(emptyMeta(limit));
      setLoading(false);
      return;
    }

    let cancelled = false;
    setLoading(true);
    setError(null);

    fleetApi
      .listDrivers(fleetId, { offset, limit })
      .then((page) => {
        if (cancelled) return;
        setData(page.data);
        setMeta(page.meta);
      })
      .catch((caught: unknown) => {
        if (cancelled) return;
        setError(toErrorMessage(caught));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [fleetId, offset, limit, reloadKey]);

  const setOffset = useCallback((next: number) => {
    setOffsetState(next < 0 ? 0 : next);
  }, []);

  const refresh = useCallback(() => setReloadKey((key) => key + 1), []);

  return { data, meta, loading, error, offset, limit, setOffset, refresh };
}
